import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {UpdateServerProcessOutput, CreateServerProcessOutput} from 'processes/dao/ProcessManagementDAO';
import {SubmitOutputsConfirmationQuery} from 'processes/tables/__generated/SubmitOutputsConfirmationQuery';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import StepNav, {StepItemStatus, StepNavVariant} from 'ui-library/StepNav';

import CreateOutputsConfirmationPane, {
  usesPaneWithoutTableForOutputCreation,
} from './CreateOutputsConfirmationPane';
import UpdateMappedOutputsConfirmationPane, {
  usesPaneWithoutTableForOutputUpdate,
} from './UpdateMappedOutputsConfirmationPane';

type ProcessOutput = ExtractByTypename<
  SubmitOutputsConfirmationQuery['processOutputs'][number],
  'ProcessOutputNode'
>;

type ProcessTask = ExtractByTypename<
  SubmitOutputsConfirmationQuery['processTasks'][number],
  'ProcessTaskNode'
>;

enum SubmitOutputsStep {
  UPDATE_TASK_STATUS = 'UPDATE_TASK_STATUS',
  CONFIRM_UPDATES_ALREADY_MAPPED = 'CONFIRM_UPDATES_ALREADY_MAPPED',
}

const SUBMIT_OUTPUTS_PANES = [
  {
    key: SubmitOutputsStep.UPDATE_TASK_STATUS,
    title: 'Update task status',
  },
  {
    key: SubmitOutputsStep.CONFIRM_UPDATES_ALREADY_MAPPED,
    title: 'Confirm updates',
  },
];

const DEFAULT_STATUS_BY_STEP = {
  [SubmitOutputsStep.UPDATE_TASK_STATUS]: StepItemStatus.UNVISITED,
  [SubmitOutputsStep.CONFIRM_UPDATES_ALREADY_MAPPED]: StepItemStatus.UNVISITED,
};

interface MultiStepSubmitOutputsConfirmationModalProps {
  tasks: Array<ProcessTask>;
  outputs: Array<ProcessOutput>;
  outputsBeingCreated: Array<CreateServerProcessOutput>;
  outputsAlreadyMappedWithUpdatedFields: Array<UpdateServerProcessOutput>;
  onUpdateTasks: (tasks: Array<ProcessTask>) => void;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export function submitOutputsConfirmationUsesPanesWithoutTables(
  props: Pick<
    MultiStepSubmitOutputsConfirmationModalProps,
    'tasks' | 'outputsBeingCreated' | 'outputsAlreadyMappedWithUpdatedFields'
  >
): boolean {
  return (
    usesPaneWithoutTableForOutputCreation({
      outputsBeingCreated: props.outputsBeingCreated,
    }) &&
    usesPaneWithoutTableForOutputUpdate({
      outputsAlreadyMappedWithUpdatedFields: props.outputsAlreadyMappedWithUpdatedFields,
    })
  );
}

export default function MultiStepSubmitOutputsConfirmationModal(
  props: MultiStepSubmitOutputsConfirmationModalProps
): JSX.Element {
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [isNextButtonEnabled, setIsNextButtonEnabled] = useState(false);

  const getModalButtons = (): Array<ModalButton> => {
    switch (currentStepIdx) {
      case 0:
        return [
          {
            text: 'Next',
            style: ButtonStyle.PRIMARY,
            disabled: !isNextButtonEnabled,
            onClick: () => {
              setCurrentStepIdx(currentStepIdx + 1);
            },
          },
        ];
      case 1:
        return [
          {
            text: 'Back',
            onClick: () => {
              setCurrentStepIdx(currentStepIdx - 1);
            },
          },
          {
            text: 'Submit',
            style: ButtonStyle.PRIMARY,
            submit: true,
            disabled: !isNextButtonEnabled,
          },
        ];
      default:
        assert(false, `Unsupported pane index ${currentStepIdx}`);
    }
  };

  const getModalBody = (): JSX.Element => {
    switch (SUBMIT_OUTPUTS_PANES[currentStepIdx].key) {
      case SubmitOutputsStep.UPDATE_TASK_STATUS:
        return (
          <CreateOutputsConfirmationPane
            tasks={props.tasks}
            outputsBeingCreated={props.outputsBeingCreated}
            onUpdateTasks={props.onUpdateTasks}
            onChangeAreQueriesCompleted={setIsNextButtonEnabled}
          />
        );
      case SubmitOutputsStep.CONFIRM_UPDATES_ALREADY_MAPPED:
        return (
          <UpdateMappedOutputsConfirmationPane
            outputsAlreadyMappedWithUpdatedFields={props.outputsAlreadyMappedWithUpdatedFields}
            outputs={props.outputs}
            onChangeAreQueriesCompleted={setIsNextButtonEnabled}
          />
        );
      default:
        assert(false, `Unsupported pane ${SUBMIT_OUTPUTS_PANES[currentStepIdx].key}`);
    }
  };

  const useLargeModal = !submitOutputsConfirmationUsesPanesWithoutTables(props);

  return (
    <Modal
      title="Submit outputs"
      expandHeight={useLargeModal}
      large={useLargeModal}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={currentStepIdx === SUBMIT_OUTPUTS_PANES.length - 1 ? props.onRequestSubmit : undefined}
      buttons={getModalButtons()}
    >
      <div className={css(styles.stepNavContainer)}>
        <StepNav
          steps={SUBMIT_OUTPUTS_PANES}
          statusByStep={DEFAULT_STATUS_BY_STEP}
          currentStepKey={SUBMIT_OUTPUTS_PANES[currentStepIdx].key}
          onUpdateCurrentStep={() => {}}
          variant={StepNavVariant.FLAT}
        />
      </div>
      <div className={css(styles.modalBody)}>{getModalBody()}</div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  stepNavContainer: {
    margin: '0 0 12px 0px',
  },
  modalBody: {
    paddingRight: '8px',
  },
});
